import React, { useMemo } from 'react';

import { GLOBAL_APP_BAR_LOGO_IMAGE } from '../../../../constants/templates';
import { IMAGE_TYPES, MAX_IMAGE_SIZE_BYTES } from '../../../../constants/upload';
import { useTemplateUploadHandlers } from '../../../../hooks/useTemplateUploadHandlers';
import { IBaseUploadControlProps, IImageUrlValueProps } from '../../../../types/templates';
import { ImageUploader } from '../FileUploader/ImageUploader';

interface IProps extends IBaseUploadControlProps, IImageUrlValueProps {
  onDrop?: (files: File[]) => void;
  onUpload?: (url: string) => void;
  onRemove: () => void;
  onClearOverride?: () => void;
  logoSizePercent?: number;
}

export const AppLogoUploader: React.FC<IProps> = ({
  mode,
  globalValue,
  overrideValue,
  defaultValue,
  onDrop: onDropProp,
  onUpload,
  onRemove,
  onClearOverride,
  logoSizePercent,
  dataAction,
}) => {
  const { isOverridden, handleDrop, handleRemoveClick } = useTemplateUploadHandlers({
    mode,
    overrideValue,
    fieldName: GLOBAL_APP_BAR_LOGO_IMAGE,
    onDrop: onDropProp,
    onUpload,
    onRemove,
    onClearOverride,
  });

  const displayUrl = useMemo(() => {
    return isOverridden ? overrideValue : globalValue || defaultValue;
  }, [isOverridden, overrideValue, globalValue, defaultValue]);

  const isCustomized = useMemo(() => {
    return !(!displayUrl || displayUrl === defaultValue);
  }, [displayUrl, defaultValue]);

  const isInherited = useMemo(() => {
    return displayUrl === defaultValue;
  }, [displayUrl, defaultValue]);

  const fileSx = useMemo(() => {
    if (typeof logoSizePercent !== 'number') {
      return undefined;
    }

    return {
      transform: `scale(${logoSizePercent / 100})`,
      transformOrigin: 'center center',
      objectFit: 'contain' as const,
    };
  }, [logoSizePercent]);

  const uploaderConfig = useMemo(() => {
    return {
      accept: IMAGE_TYPES,
      maxSize: MAX_IMAGE_SIZE_BYTES,
      height: 150,
      fileUrl: displayUrl || undefined,
      fileSx,
      onDrop: handleDrop,
      onRemove: handleRemoveClick,
      showRemove: isCustomized,
      changeButtonTooltip: 'SVG, PNG, or JPG (max. 5MB)',
      isInherited,
      dataAction,
    };
  }, [displayUrl, fileSx, handleDrop, handleRemoveClick, isCustomized, isInherited, dataAction]);

  return <ImageUploader {...uploaderConfig} />;
};
